/** Color variant for styled content blocks */
export type BlockVariant = "default" | "blue" | "green" | "yellow" | "purple" | "red";

/** A styled content block within a section */
export type ContentBlock = {
  /** Markdown text rendered inside the block */
  text: string;
  /** Background color variant (default = no background) */
  variant?: BlockVariant;
};

/** JSON structure stored in consent_types.description for terms_and_condition */
export type TermsSection = {
  title: string;
  /** Markdown body (used when blocks is absent) */
  content: string;
  /** Optional styled content blocks. When present, rendered instead of content. */
  blocks?: ContentBlock[];
};

export type TermsData = {
  version: string;
  effectiveDate: string;
  lastUpdated: string;
  entity: string;
  /** Markdown intro shown in the blue callout */
  intro: string;
  sections: TermsSection[];
};

/** Default T&C content matching the approved prototype */
export const DEFAULT_TERMS_DATA: TermsData = {
  version: "1.0",
  effectiveDate: "09 April 2026",
  lastUpdated: "09 April 2026",
  entity: "Dwell Collective Pte. Ltd.",
  intro:
    'These Terms & Conditions govern the purchase, issuance, sharing, access, and redemption of promotions, vouchers, offers, and referral incentives made available by participating merchants through the Dwell platform operated by **Dwell Collective Pte. Ltd.**',
  sections: [
    {
      title: "1. Definitions",
      content: [
        '**"Deal"** refers to any promotion, discount, voucher, reward, referral incentive, or limited-time offer published by a Venue via the Dwell platform.',
        '**"Venue"** refers to merchants, retailers, F&B outlets, or service providers participating in the Dwell platform.',
        '**"User"** refers to individuals accessing, sharing, or redeeming Deals.',
        '**"Referral Reward"** refers to any incentive provided to Users when a shared Deal is redeemed by another User.',
      ].join("\n\n"),
    },
    {
      title: "2. General Terms",
      content: [
        "- All Deals displayed on the Dwell platform are offered solely by the Venue unless otherwise stated.",
        "- **Dwell acts only as a facilitator** and is not the seller or provider of any goods or services offered under any Deal.",
        "- Use of the Dwell platform is subject to these Terms and any other applicable policies, including our Privacy Policy.",
        "- Dwell may, at its sole discretion, amend, modify, or withdraw any Deal or feature at any time without notice.",
      ].join("\n"),
    },
    {
      title: "3. Validity and Use of Deals",
      content: [
        "**3.1 Validity Period**\n\nEach Deal is subject to a validity period as specified on the platform. Expired Deals will not be honored.",
        "**3.2 Redemption Conditions**\n\nDeals are redeemable only:\n- At the specified Venue(s)\n- During the stated validity period\n- In accordance with all conditions listed on the Deal page\n- Upon verification by the Venue through the Dwell platform",
        "**3.3 Non-Transferability**\n\nUnless otherwise stated, Deals are non-transferable, non-exchangeable, and non-refundable.",
        "**3.4 No Cash Value**\n\nDeals have no cash value and cannot be exchanged for cash, credit, or other items unless expressly stated.",
        "**3.5 Multi-Use Redemption**\n\nMost Deals can be redeemed multiple times unless explicitly stated as single-use. Deals may not be combined with other promotions, loyalty schemes, or Venue discounts unless permitted by the Venue.",
        "**3.6 Venue Discretion**\n\nVenues reserve the right to decline redemption if:\n- The Deal has expired\n- Conditions are not met\n- There is suspicion of misuse or fraudulent activity\n- Operational constraints prevent fulfilment (e.g., sold-out items)",
      ].join("\n\n"),
    },
    {
      title: "4. Sharing, Referral Links, and Rewards",
      content: [
        "**4.1 Sharing of Deals**\n\nUsers may share Deals through unique links or QR codes provided by the platform.",
        "**4.2 Referral Reward Issuance**\n\nReferral Rewards will be issued only upon:\n- A valid redemption by another User\n- Verification by the Venue\n- Compliance with all Dwell policies",
        "> **4.3 Non-Cash Nature of Rewards**\n>\n> Referral Rewards may be issued in points, credits, or platform-based benefits. These rewards may have an equivalent value when redeemed at participating venues, but they **do not represent cash**, are not legal tender, cannot be withdrawn as cash, and cannot be exchanged for cash unless expressly stated. The use, value, and applicability of such rewards are limited strictly to the terms specified within the Dwell platform.",
        "**4.4 Fraudulent Activity**\n\nDwell may suspend or void rewards if fraudulent, abusive, or manipulative behavior is detected, including but not limited to:\n- Self-referrals\n- Use of fake or duplicate accounts\n- Automated or mass distribution of links\n- Manipulation of redemption flows",
        "**4.5 Right to Modify or Terminate Rewards**\n\nDwell may modify, suspend, or terminate the rewards system at any time without prior notice.",
      ].join("\n\n"),
    },
    {
      title: "5. Points & Rewards",
      content: [
        "**Earning Points:**\n- Points are awarded upon successful voucher redemption at venues\n- Referral points are credited when shared vouchers are redeemed by friends\n- Direct redemption points are earned when you use a voucher yourself\n- Points are specific to each venue and can only be used at that venue",
        "**Using Points:**\n- Points can be redeemed for vouchers at participating venues\n- Point values and redemption rates are set by individual venues\n- Points do not expire unless stated otherwise by the venue",
      ].join("\n\n"),
    },
    {
      title: "6. Venue Responsibilities",
      content:
        "Venues are solely responsible for:\n- The accuracy of Deal information\n- The provision and quality of goods/services\n- Enforcing redemption rules\n- Compliance with all applicable laws, regulations, and licensing requirements\n\nAny dispute relating to product/service quality shall be resolved directly between the User and the Venue.",
    },
    {
      title: "7. User Responsibilities",
      content:
        "Users agree to use the platform lawfully and not engage in:\n- Abuse or misuse of Deals\n- Fraudulent activity\n- Misrepresentation or impersonation\n- Conduct that disrupts the platform or harms Venues\n\nUsers are responsible for ensuring Deal terms and redemption conditions are met.",
    },
    {
      title: "8. Expiration & Validity",
      content:
        "- Vouchers expire on the date specified in the promotion\n- Expired vouchers cannot be used or extended\n- QR codes are valid for 5 minutes from generation time\n- New QR codes can be generated as needed before voucher expiry",
    },
    {
      title: "9. Personal Data Protection",
      content:
        "Dwell complies with the **Singapore Personal Data Protection Act (PDPA)**.\n\nPersonal data is collected for purposes including:\n- Deal access and redemption\n- Fraud detection\n- Reward issuance\n- Platform and Venue analytics\n\nDwell does not disclose personal data to Venues unless:\n- Required for redemption verification, or\n- Explicit consent is provided by the User\n\nUsers may refer to the Dwell Privacy Policy for full details.",
    },
    {
      title: "10. Limitation of Liability",
      content:
        "To the maximum extent permitted by law, Dwell:\n- Makes no representations or warranties regarding the accuracy, availability, or quality of any Deal\n- Shall not be liable for any refusal by a Venue to honor a Deal\n- Shall not be responsible for any injury, loss, damages, or expenses arising from goods/services provided by Venues\n\nDwell shall not be liable for any indirect, incidental, punitive, or consequential damages.\n\n**Dwell's total aggregate liability arising from any Deal shall not exceed SGD 10.**",
    },
    {
      title: "11. Modifications & Cancellations",
      content:
        "Dwell may revise these Terms at any time. The revised Terms will take effect upon posting on the platform. Continued use of the platform constitutes acceptance of the updated Terms.\n\nDwellDrop and participating venues reserve the right to:\n- Modify or cancel promotions at any time\n- Update terms and conditions with notice to users\n- Suspend or terminate accounts for violations of terms\n- Change point values and redemption rates",
    },
    {
      title: "12. Suspension or Termination",
      content:
        "Dwell may suspend or terminate a User's access without notice if:\n- Fraudulent or abusive behavior is suspected\n- Terms are breached\n- Required by law, operational, or security reasons\n\nAny unused Deals or Rewards may be cancelled without compensation.",
    },
    {
      title: "13. Governing Law",
      content:
        "These Terms are governed by the laws of Singapore.\n\nAny disputes shall be submitted to the exclusive jurisdiction of the courts of Singapore.",
    },
    {
      title: "14. Contact Information",
      content:
        "For inquiries or assistance, please contact:\n\n**Email:** support@dwelldrop.com.sg\n**Location:** Singapore",
    },
  ],
};

/** Default Privacy Policy content matching the approved prototype */
export const DEFAULT_PRIVACY_DATA: TermsData = {
  version: "2.0",
  effectiveDate: "09 April 2026",
  lastUpdated: "09 April 2026",
  entity: "Dwell Collective Pte. Ltd.",
  intro:
    'This Privacy Policy explains how **Dwell Collective Pte. Ltd.** ("we", "us", "our") collects, uses, discloses, and protects your personal data in compliance with Singapore\'s Personal Data Protection Act 2012 (PDPA).\n\n**You Have Full Control**\n\nWe\'ve built a **granular consent management system** that gives you complete control over your data. Toggle any optional consent ON or OFF anytime in **Settings \u2192 Privacy & Consent**. Your choices are saved instantly and respected across the entire platform.',
  sections: [
    /* ---- Section 1: no colored boxes ---- */
    {
      title: "1. Information We Collect",
      content: "",
      blocks: [
        {
          text: "**Account Information**\n- Username (required)\n- Full name (required)\n- Mobile phone number (required)\n- Password (encrypted)",
        },
        {
          text: "**Usage Data**\n- Vouchers saved and redeemed\n- Referral links shared and clicked\n- Points earned and spent\n- Points transactions",
        },
        {
          text: "**Device & Technical Data**\n- IP address (hashed, not stored in raw form)\n- Browser type and version\n- Device type (mobile, tablet, desktop)\n- Operating system",
        },
        {
          text: "**Optional Data (With Your Consent)**\n- Location data (GPS coordinates for nearby recommendations)\n- App usage analytics (feature usage, session duration)\n- Marketing preferences (SMS, push notifications)",
        },
      ],
    },
    /* ---- Section 2: blue / purple / yellow / green boxes ---- */
    {
      title: "2. How We Use Your Information",
      content: "",
      blocks: [
        {
          variant: "blue",
          text: "**Referral Program Operation (Required)**\n\nLegal Basis: Consent (essential for platform functionality)\n- Track referral link clicks and redemptions\n- Calculate and distribute points rewards\n- Attribute vouchers to correct referrers\n- Prevent fraud and abuse\n\nData Retention: 7 years (for compliance and fraud prevention)",
        },
        {
          variant: "purple",
          text: "**Marketing Communications (Optional)**\n\nLegal Basis: Explicit opt-in consent\n- Send promotional SMS about new deals\n- Push notifications for nearby offers\n- Personalized venue recommendations\n\nData Retention: 2 years after consent withdrawal\n\n*You can toggle this in Settings anytime*",
        },
        {
          variant: "yellow",
          text: "**Usage Analytics (Optional)**\n\nLegal Basis: Legitimate interest\n- Improve app features and user experience\n- Identify technical issues and bugs\n- Understand user behavior patterns\n\nData Retention: 1 year",
        },
        {
          variant: "green",
          text: "**Fraud Detection & Prevention**\n\nLegal Basis: Legitimate interest\n- Detect suspicious referral patterns\n- Prevent QR code manipulation\n- Monitor for abuse of points system\n- Protect venue partners from fraud\n\nData Retention: 7 years (for investigation and compliance)",
        },
      ],
    },
    /* ---- Section 3: default + green + blue + purple boxes ---- */
    {
      title: "3. Your Consent & Rights (PDPA)",
      content: "",
      blocks: [
        {
          text: "**5 Consent Types You Control**\n\n1. **Referral Link Tracking** (Required) \u2014 Essential for points calculations and fraud prevention\n2. **Marketing Communications** (Optional) \u2014 Promotional SMS, push notifications\n3. **Usage Analytics** (Optional) \u2014 App usage patterns to improve UX\n4. **Location Services** (Optional) \u2014 Show nearby deals based on your location\n5. **Personalized Recommendations** (Optional) \u2014 Activity history-based suggestions",
        },
        {
          variant: "green",
          text: "**Your PDPA Rights**\n\n1. **Right of Access:** Request a copy of all your personal data (30-day response)\n2. **Right of Correction:** Request corrections to inaccurate data (10-day response)\n3. **Right of Erasure:** Request deletion of your account and data (30-day completion)\n4. **Right of Portability:** Export your data in JSON/CSV format (30-day response)\n5. **Right of Objection:** Object to specific data processing activities (10-day response)\n6. **Right to Withdraw Consent:** Instantly toggle OFF any optional consent in Settings (takes effect immediately)\n\nTo exercise these rights, contact: privacy@dwellcollective.co",
        },
        {
          variant: "blue",
          text: "**Real-Time Consent Management**\n\nUnlike traditional privacy policies that require emailing support, **our system lets you manage consents instantly**:\n- Toggle consent switches in Settings \u2192 Privacy & Consent\n- Changes saved to database with full audit trail\n- All app features respect your consent preferences immediately\n- No waiting, no emails, no hassle\n\nThis is privacy by design in action.",
        },
        {
          variant: "purple",
          text: "**Consent Audit Trail**\n\nEvery consent action is logged with timestamp, version, and method (explicit/implicit). When we update consent terms, you\u2019ll be prompted to review and re-accept. Your consent history is preserved for 7 years for regulatory compliance.",
        },
      ],
    },
    /* ---- Section 4: default + green + red boxes ---- */
    {
      title: "4. Data Security & Protection",
      content: "",
      blocks: [
        {
          text: "**Technical Security Measures**\n- **HTTPS Encryption:** All data transmitted over TLS 1.3+ encryption\n- **Password Security:** Passwords hashed using industry-standard algorithms\n- **IP Address Hashing:** Raw IP addresses never stored, only SHA-256 hashes for audit trail\n- **Row-Level Security (RLS):** Database policies ensure users can only access their own data\n- **Consent Database:** Separate secure tables for consent tracking with immutable audit logs\n- **No PII in Logs:** Client-side logs anonymized, server logs rotate every 30 days",
        },
        {
          variant: "green",
          text: "**PDPA Technical Compliance**\n\nOur consent management system meets PDPA requirements through:\n- **Granular Consent:** Each data processing purpose has its own consent toggle\n- **Explicit Consent:** Clear descriptions of what each consent enables\n- **Consent Withdrawal:** One-click withdrawal takes effect immediately\n- **Version Control:** Track consent changes over time with full history\n- **Data Cleanup:** Withdrawing consent triggers automated data deletion for that purpose\n- **Audit Trail:** Every consent action logged with timestamp and IP hash for compliance verification",
        },
        {
          variant: "red",
          text: "**Data Breach Notification**\n\nIn the unlikely event of a data breach, we will notify affected users and the Singapore PDPC within 72 hours, as required by PDPA.",
        },
      ],
    },
    /* ---- Section 5: default + yellow box ---- */
    {
      title: "5. Who We Share Your Data With",
      content: "",
      blocks: [
        {
          text: "**We Share Data With:**\n- **Venue Partners:** Minimum data required for redemption verification (username, voucher details)\n- **Referrers:** When you redeem a shared voucher, the referrer sees that their link was used (username only, no contact info)\n- **Service Providers:** Supabase (database hosting in Singapore), essential for platform operation",
        },
        {
          variant: "yellow",
          text: "**We Never Share:**\n- Your mobile number with other users\n- Your password (encrypted and never accessible)\n- Your data with third-party advertisers\n- Your data for purposes outside those listed above",
        },
      ],
    },
    /* ---- Section 6: no colored boxes ---- */
    {
      title: "6. How Long We Keep Your Data",
      content:
        "**Data Retention Periods:**\n\n- **Referral & points data:** 7 years\n- **Fraud detection logs:** 7 years\n- **Marketing consent:** 2 years after withdrawal\n- **Usage analytics:** 1 year\n- **Location data:** 90 days\n- **Account data (after deletion request):** 30 days\n\nAfter deletion, anonymized transaction records are retained for 7 years for regulatory compliance (no PII).",
    },
    /* ---- Section 7: default + green box ---- */
    {
      title: "7. Cookies & Similar Technologies",
      content: "",
      blocks: [
        {
          text: "**Essential Cookies (Always Active)**\n- Session authentication (keeps you logged in)\n- Security tokens (prevents CSRF attacks)\n- Local preferences (app settings)",
        },
        {
          variant: "green",
          text: "**No Third-Party Cookies:** We do not use cookies from advertising networks or analytics platforms. All data storage is first-party only.",
        },
      ],
    },
    /* ---- Sections 8-11: plain content, no colored boxes ---- */
    {
      title: "8. Children's Privacy",
      content:
        "Dwell is intended for users aged 18 and above. We do not knowingly collect personal data from minors. If you are under 18, please do not use this platform or provide any personal information.",
    },
    {
      title: "9. International Data Transfers",
      content:
        "Your data is primarily stored in Singapore. If we transfer data internationally, we ensure adequate protection through:\n- Standard contractual clauses approved by Singapore PDPC\n- Adequacy assessments of destination countries\n- Encryption during transfer",
    },
    {
      title: "10. Your Responsibilities",
      content:
        "- Keep your password secure and do not share it with anyone\n- Log out from shared devices after use\n- Review your consent settings regularly in Settings\n- Report suspicious activity or unauthorized access immediately\n- Keep your mobile number updated for security notifications",
    },
    {
      title: "11. Changes to This Policy",
      content:
        "We may update this Privacy Policy from time to time. When we do:\n- You\u2019ll receive an in-app notification\n- The \"Effective Date\" at the top will be updated\n- For material changes, you\u2019ll be asked to review and re-accept\n- Your continued use constitutes acceptance of minor updates",
    },
    /* ---- Section 12: blue + green boxes ---- */
    {
      title: "12. Contact & Data Protection Officer",
      content: "",
      blocks: [
        {
          variant: "blue",
          text: "**For Privacy Inquiries, Rights Requests, or Concerns:**\n\n**Email:** privacy@dwellcollective.co\n**Data Protection Officer:** [DPO Name]\n**Company:** Dwell Collective Pte. Ltd.\n**Address:** [Singapore Business Address]\n**Response Time:** Within 30 days for data access/deletion requests, 10 days for correction requests",
        },
        {
          variant: "green",
          text: "**Singapore Personal Data Protection Commission**\n\nIf you are not satisfied with our response, you may lodge a complaint with the PDPC at www.pdpc.gov.sg",
        },
      ],
    },
  ],
};

/** Safely parse a JSON string into TermsData, falling back to defaults */
export function parseTermsData(raw: string): TermsData {
  if (!raw.trim()) return DEFAULT_TERMS_DATA;
  try {
    const parsed = JSON.parse(raw) as TermsData;
    if (parsed.sections && Array.isArray(parsed.sections)) return parsed;
    return DEFAULT_TERMS_DATA;
  } catch {
    return DEFAULT_TERMS_DATA;
  }
}

/** Safely parse a JSON string into TermsData for privacy policy, falling back to privacy defaults */
export function parsePrivacyData(raw: string): TermsData {
  if (!raw.trim()) return DEFAULT_PRIVACY_DATA;
  try {
    const parsed = JSON.parse(raw) as TermsData;
    if (parsed.sections && Array.isArray(parsed.sections)) return parsed;
    return DEFAULT_PRIVACY_DATA;
  } catch {
    return DEFAULT_PRIVACY_DATA;
  }
}

/**
 * Parse a consent/policy description stored as structured JSON.
 * Returns null for plain Markdown — never substitutes default content, so a
 * historical snapshot is always shown as it was stored.
 */
export function parseStructuredPolicy(raw: string): TermsData | null {
  if (!raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<TermsData>;
    return Array.isArray(parsed.sections) ? (parsed as TermsData) : null;
  } catch {
    return null;
  }
}

/** Short plain-text preview of a consent description (JSON or Markdown). */
export function policyPreviewText(raw: string): string {
  const data = parseStructuredPolicy(raw);
  const first = data?.sections[0];
  const text = data
    ? data.intro?.trim() ||
      first?.blocks?.map((b) => b.text).join(" ") ||
      first?.content ||
      ""
    : raw;
  return text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`>#]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
